import { CoObjectGeneralInfo, CoObjectItem, SheetForm } from "../../interfaces/CoObject";
import { CoObjectsGeneralInfo } from "../CoObjectsGeneralInfoPanel";
import { SourceTypeEnum } from "../../../api/models/source-type-enum";
import { CoObjectParametersTablePanel } from "../CoObjectParametersTablePanel";
import React, { Dispatch, SetStateAction } from "react";
import { ObjectPanelTabs, TableUpdateStrategy } from "./ObjectPanelComponent";
import { SourcePanelContainer } from "./SourcePanelContainer";
import { AdvancedPanelContainer } from "./AdvancedPanelContainer";
import { mergeStyleSets } from "@fluentui/react/lib/Styling";
import { CoObjectDimensionsTextPanel } from "../tgk-text/CoObjectDimensionsTextPanel";
import { DatasetField } from "../../../api/models/dataset-field";
import { DimensionDetail } from "../../../api/models/dimension-detail";
import { AnalyticalAmountDimensionsPanel } from "../co-amount-analytical-panel/AnalyticalAmountDimensionsPanel";
import { AvailableDimensions, AvailableParameters } from "./ObjectPanelComponentHook";
import { DocumentPartMetadata } from "../../../api/models/document-part-metadata";
import { Scenario } from "../../../api/models/scenario";
import { ComponentReference } from "../../../api/models/component-reference";

const classNames = mergeStyleSets({
  breadcrumb: {
    color: "var(--color-blue)",
    fontWeight: "bold",
    padding: "8px",
  },
});

export interface ContentObjectProps {
  isEdit: boolean;
  isLocked: boolean;
  isComponentInstance: boolean;
  updateWithComponentData: (component: ComponentReference) => Promise<void>;
  selectedKey: number;
  getBreadcrumbTitle: () => string;
  documentPartMetadata: DocumentPartMetadata;
  objectInfo: CoObjectGeneralInfo;
  objectSource: any;
  objectAdvanced: any;
  objectParameters: any;
  updateObjectInfo: (
    obj,
    tabValid,
    modified,
    modifiedType?: boolean,
    modifiedSources?: boolean,
    tableUpdateStrategy?: TableUpdateStrategy
  ) => void;
  updateObjectSource: (obj, tabValid, modified) => void;
  updateObjectParameters: (obj, tabValid, modified) => void;
  updateObjectAdvanced: (obj, tabValid, modified) => void;
  onPasteSmartObject: (smartObject: CoObjectItem) => void;
  items: ObjectPanelTabs[];
  availableParametersMetadata: AvailableParameters;
  inheritableParameters: DimensionDetail[] | undefined;
  datasetFields: DatasetField[] | undefined;
  isFileExcelExisting?: boolean;
  fileExcelName?: string;
  updateFileExcelName?: (fileExcelName: string) => void;
  originalSheetArea?: SheetForm;
  setOriginalSheetArea?: Dispatch<SetStateAction<SheetForm>>;
  setIsOldSheetOrAreaSelected?: Dispatch<SetStateAction<boolean>>;
  setIsLoadingSheetAndArea?: Dispatch<SetStateAction<boolean>>;
  setIsSheetChartAndAreaToHide?: Dispatch<SetStateAction<boolean>>;
  isSheetChartAndAreaToHide?: boolean;
  referringScenarios?: Scenario;
  availableDimensionsMetadata: AvailableDimensions;
}

export const ContentObject = (props: ContentObjectProps) => {
  const {
    isEdit,
    isLocked,
    isComponentInstance,
    updateWithComponentData,
    selectedKey,
    getBreadcrumbTitle,
    documentPartMetadata,
    objectInfo,
    updateObjectInfo,
    objectSource,
    updateObjectSource,
    objectAdvanced,
    updateObjectAdvanced,
    objectParameters,
    updateObjectParameters,
    onPasteSmartObject,
    items,
    availableParametersMetadata,
    inheritableParameters,
    datasetFields,
    isFileExcelExisting,
    fileExcelName,
    updateFileExcelName,
    originalSheetArea,
    setOriginalSheetArea,
  } = props;

  const getRenderedPanel = () => {
    if (objectInfo || !isEdit) {
      switch (selectedKey) {
        case 0:
          return (
            <CoObjectsGeneralInfo
              tab={items[0]}
              isEdit={isEdit}
              isLocked={isLocked}
              isComponentInstance={isComponentInstance}
              updateWithComponentData={updateWithComponentData}
              objectInfo={objectInfo}
              updateCoObjectInfo={updateObjectInfo}
              objectSource={objectSource}
              isFileExcelExisting={isFileExcelExisting}
            />
          );
        case 1:
          return (
            <SourcePanelContainer
              isEdit={isEdit}
              isLocked={isLocked}
              isComponentInstance={isComponentInstance}
              selectedKey={selectedKey}
              objectType={objectInfo ? objectInfo.objectType : null}
              objectSource={objectSource}
              updateObjectSource={updateObjectSource}
              tab={items[1]}
              inheritableParameters={inheritableParameters}
              datasetFields={datasetFields}
              documentPartMetadata={documentPartMetadata}
              isFileExcelExisting={isFileExcelExisting}
              fileExcelName={fileExcelName}
              updateFileExcelName={updateFileExcelName}
              onPasteSmartObject={onPasteSmartObject}
              originalSheetArea={originalSheetArea}
              setOriginalSheetArea={setOriginalSheetArea}
              setIsOldSheetOrAreaSelected={props.setIsOldSheetOrAreaSelected}
              setIsLoadingSheetAndArea={props.setIsLoadingSheetAndArea}
              setIsSheetChartAndAreaToHide={props.setIsSheetChartAndAreaToHide}
              isSheetChartAndAreaToHide={props.isSheetChartAndAreaToHide}
              referringScenarios={props.referringScenarios}
              availableDimensionsMetadata={props.availableDimensionsMetadata}
            />
          );
        case 2:
          if (
            ((objectInfo.objectType === "TABLE" || objectInfo.objectType === "CHART") &&
              objectSource.objectSource !== SourceTypeEnum.EXCEL) ||
            (objectInfo.objectType === "AMOUNT" && objectSource.objectSource === SourceTypeEnum.TAGETIK_FORM)
          )
            return (
              <CoObjectParametersTablePanel
                tab={items[2]}
                isEdit={isEdit}
                isLocked={isLocked}
                isComponentInstance={isComponentInstance}
                objectParameters={objectParameters}
                updateCoObjectParametersTable={(obj, tabValid, modified) =>
                  updateObjectParameters(obj, tabValid, modified)
                }
                dbApplication={objectSource.dbApplication}
                formId={objectSource.form?.code}
                availableParametersMetadata={availableParametersMetadata}
                inheritableParameters={inheritableParameters}
                referringScenarios={props.referringScenarios}
              />
            );
          else if (objectInfo.objectType === "TGK_TEXT")
            return (
              <CoObjectDimensionsTextPanel
                tab={items[2]}
                isEdit={isEdit}
                isLocked={isLocked}
                isComponentInstance={isComponentInstance}
                datasetFields={datasetFields}
                inheritableParameters={objectInfo?.componentReference !== undefined ? undefined : inheritableParameters}
                objectSource={objectSource}
                updateCoObjectSourceText={(obj, tabValid, modified) => updateObjectSource(obj, tabValid, modified)}
                referringScenarios={props.referringScenarios}
              />
            );
          else if (objectInfo.objectType === "AMOUNT" && objectSource?.analyticalWorkspace?.code)
            return (
              <AnalyticalAmountDimensionsPanel
                tab={items[2]}
                isEdit={isEdit}
                isLocked={isLocked}
                isComponentInstance={isComponentInstance}
                datasetFields={datasetFields}
                inheritableParameters={objectInfo?.componentReference !== undefined ? undefined : inheritableParameters}
                objectSource={objectSource}
                updateCoObjectSourceAmount={(obj, tabValid, modified) => updateObjectSource(obj, tabValid, modified)}
                referringScenarios={props.referringScenarios}
              />
            );
          else
            return (
              <AdvancedPanelContainer
                isEdit={isEdit}
                isLocked={isLocked}
                isComponentInstance={isComponentInstance}
                objectAdvanced={objectAdvanced}
                updateObjectAdvanced={updateObjectAdvanced}
                objectType={objectInfo ? objectInfo.objectType : null}
                objectSource={objectSource.objectSource}
                tab={items[2]}
                documentPartMetadata={documentPartMetadata}
                isFileExcelExisting={isFileExcelExisting}
              />
            );
        case 3:
          return (
            <AdvancedPanelContainer
              isEdit={isEdit}
              isLocked={isLocked}
              isComponentInstance={isComponentInstance}
              objectAdvanced={objectAdvanced}
              updateObjectAdvanced={updateObjectAdvanced}
              objectType={objectInfo ? objectInfo.objectType : null}
              objectSource={objectSource.objectSource}
              tab={items[items.length - 1]}
              documentPartMetadata={documentPartMetadata}
              isFileExcelExisting={isFileExcelExisting}
            />
          );
      }
    }
  };

  return (
    <>
      {!isEdit && !isComponentInstance ? <div className={classNames.breadcrumb}>{getBreadcrumbTitle()}</div> : null}
      <div style={{ marginBottom: "20px" }}>{getRenderedPanel()}</div>
    </>
  );
};
